/**
 * LAB-4507 — `useEndpoints(asset)` data hook.
 *
 * Owns the fetch → extract → parse → download pipeline for the Endpoints
 * sub-tab, keeping the section component presentational. Reuses `useGetFile`
 * (which already does the presigned-URL fetch + JSON parse) exactly as the
 * sibling `SitemapTab` does inline.
 */

import { useGetFile } from '@/hooks/useFiles';
import type {
  EndpointRow,
  EndpointsSummary,
} from '@/sections/asset/types/endpoints.types';
import { downloadOpenApiYaml } from '@/sections/asset/utils/downloadOpenApiYaml';
import {
  EndpointsParseError,
  openapiToEndpoints,
} from '@/sections/asset/utils/openapiToEndpoints';

/** Minimal asset shape this hook needs (tolerates snake/camel details path). */
interface EndpointsAsset {
  key?: string;
  identifier?: string;
  details_path?: string;
  detailsPath?: string;
  /** RFC3339 "last seen" — when Vespasian last scanned this web application. */
  visited?: string;
}

interface WebApplicationDetailsBlob {
  spec?: string;
  spec_format?: string;
  openapi?: string; // legacy: older records stored the spec under this key
}

const EMPTY_SUMMARY: EndpointsSummary = {
  endpointCount: 0,
  methodCount: 0,
  unauthenticated: null,
  lastCaptured: null,
};

/**
 * Future enhancement: when an asset has multiple child WebApplications, a
 * deterministic picker (prefer non-empty details_path, then smallest key) could
 * select the target. Not needed today — the sub-tab is gated on
 * `isWebApplication(asset)`, so the drawer's own asset is always the target and a
 * single details_path resolution is correct.
 */

/** Extract + normalize the details path (mirrors SitemapTab.tsx:152-166). */
const resolveDetailsPath = (asset: EndpointsAsset): string => {
  const rawPath = asset.details_path ?? asset.detailsPath;
  if (!rawPath) return '';

  const normalized = rawPath.trim();
  if (
    !normalized ||
    normalized.toLowerCase() === 'null' ||
    normalized.toLowerCase() === 'undefined'
  ) {
    return '';
  }
  return normalized;
};

/** Pull the OpenAPI YAML string out of the details blob (`spec` + `spec_format`, legacy `openapi`). */
const extractOpenAPI = (
  details: WebApplicationDetailsBlob | string | null | undefined
): string => {
  if (!details) return '';

  let parsed: WebApplicationDetailsBlob | null = null;
  if (typeof details === 'string') {
    try {
      parsed = JSON.parse(details) as WebApplicationDetailsBlob;
    } catch {
      parsed = null;
    }
  } else if (typeof details === 'object') {
    parsed = details;
  }

  // The generated spec is stored under `spec` (its type named by `spec_format`);
  // older records used a top-level `openapi` key. Only treat `spec` as OpenAPI
  // when the format says so (don't feed graphql/wsdl to the OpenAPI parser),
  // falling back to the legacy key.
  const format = parsed?.spec_format?.toLowerCase();
  const isOpenApi = !format || format === 'openapi';
  const spec = (isOpenApi ? parsed?.spec : undefined) ?? parsed?.openapi;
  return spec && spec.trim() ? spec : '';
};

const getHostname = (url: string | undefined): string => {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

export interface UseEndpointsResult {
  endpoints: EndpointRow[];
  summary: EndpointsSummary;
  rawSpec: string;
  status: 'error' | 'success' | 'pending';
  isFetching: boolean;
  error?: EndpointsParseError;
  download: () => void;
}

export function useEndpoints(asset: EndpointsAsset): UseEndpointsResult {
  const detailsPath = resolveDetailsPath(asset);

  const { data, isFetching, status } = useGetFile<
    WebApplicationDetailsBlob | string
  >({ name: detailsPath }, { enabled: Boolean(detailsPath), retry: 0 });

  const rawSpec = extractOpenAPI(data);

  // No spec present is "nothing to show", not a parse failure — skip the parser.
  const parsed = rawSpec
    ? openapiToEndpoints(rawSpec)
    : { endpoints: [], summary: EMPTY_SUMMARY, error: undefined };

  const hostname = getHostname(asset.identifier);

  return {
    endpoints: parsed.endpoints,
    // "Last captured" = when Vespasian last scanned this web application.
    summary: { ...parsed.summary, lastCaptured: asset.visited?.trim() || null },
    rawSpec,
    // A disabled query (no details_path) is idle, not loading. TanStack Query v5
    // reports status 'pending' while disabled, which would leave CanonicalTable
    // stuck on a skeleton and never render the empty state (gated on 'success').
    // Treat "nothing to fetch" as resolved-empty so the NoData state shows.
    status: (detailsPath
      ? (status ?? 'pending')
      : 'success') as UseEndpointsResult['status'],
    isFetching: Boolean(isFetching),
    error: parsed.error,
    download: () => downloadOpenApiYaml(rawSpec, hostname),
  };
}
